/**
 * To buy — the shopping list.
 *
 * Two things feed it and they answer different questions. Wants raised by saved outfits are
 * reactive: they can only name something a look you already built happened to need, and they
 * carry an "unblocks" count because that is real evidence. The Work out what's missing button
 * is the other direction — it reads the whole wardrobe against the whole style guide and every
 * occasion, and names what is missing before an outfit goes looking for it.
 *
 * Rebuilt 12 Aug 2026. Brian: "This is terrible styling in that there is no list view. There is
 * no URL to click through as a suggestion. There's no assume price or price range. The literal
 * only thing I can do is hit remove... I can't sort the data. I can't filter the data. I just
 * see twenty three things all in list view with a few attribute tags."
 *
 * All of that was fair. Two notes on what the fix is NOT:
 *
 * - The links are SEARCHES, built server-side from the description, never AI-picked product
 *   pages. A generated product URL is dead or wrong often enough to be worse than no link, as
 *   an Orion belt recommendation demonstrated on him the same afternoon. The `url` field is
 *   there for a page he found himself.
 * - Sorting and filtering only became honest after the data was fixed. 13 of 23 rows had a
 *   `category` that was a layer name or free text, so a filter shipped before the 12 Aug
 *   cleanup would have been a nicer face on nonsense.
 */
import { useMemo, useState } from 'react';
import { api, money } from '../../lib/api';
import type { WardrobeCategory, WardrobeGap, WardrobeItem } from '../../shared/types';
import GapPhotoField from './GapPhotoField';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  TextInput,
  cx,
  errorText,
  gapPhotoSrc,
  useResource,
} from './ui';

const PRIORITY: Record<number, { label: string; tone: 'over' | 'near' | undefined }> = {
  1: { label: 'Buy next', tone: 'over' },
  2: { label: 'Worth having', tone: 'near' },
  3: { label: 'Refinement', tone: undefined },
};

type SortKey = 'unblocks' | 'priority' | 'price-low' | 'price-high' | 'category' | 'name';
type View = 'cards' | 'table';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'unblocks', label: 'What it unlocks' },
  { value: 'priority', label: 'Priority' },
  { value: 'price-low', label: 'Cheapest first' },
  { value: 'price-high', label: 'Dearest first' },
  { value: 'category', label: 'Category' },
  { value: 'name', label: 'Name' },
];

/**
 * A range as one string. Equal ends collapse to a single figure, which is the normal case for
 * the twelve estimates that predate the range — they carried across as low == high rather than
 * having a fake spread invented around them.
 */
function priceText(g: WardrobeGap): string | null {
  const { price_low_cents: lo, price_high_cents: hi } = g;
  if (lo === null && hi === null) return null;
  if (lo !== null && hi !== null) return lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`;
  return `about ${money((lo ?? hi)!)}`;
}

interface Bundle {
  gaps: WardrobeGap[];
  categories: WardrobeCategory[];
}

export default function GapsPanel() {
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [view, setView] = useState<View>('cards');
  const [sort, setSort] = useState<SortKey>('unblocks');
  const [priority, setPriority] = useState('all');
  const [category, setCategory] = useState('all');
  const [layer, setLayer] = useState('all');

  const [editing, setEditing] = useState<number | null>(null);
  const [buying, setBuying] = useState<number | null>(null);

  const { data, loading, error, reload, setData } = useResource<Bundle>(async () => {
    const [gaps, categories] = await Promise.all([
      api.get<WardrobeGap[]>('/wardrobe/gaps'),
      api.get<WardrobeCategory[]>('/wardrobe/categories'),
    ]);
    return { gaps, categories };
  }, []);

  const catMeta = useMemo(() => {
    const m = new Map<string, WardrobeCategory>();
    for (const c of data?.categories ?? []) m.set(c.slug, c);
    return m;
  }, [data]);

  const open = useMemo(
    () => (data?.gaps ?? []).filter((g) => g.purchased_at === null),
    [data]
  );

  const layers = useMemo(() => {
    const s = new Set<string>();
    for (const g of open) {
      const l = g.category ? catMeta.get(g.category)?.layer : undefined;
      if (l) s.add(l);
    }
    return [...s].sort();
  }, [open, catMeta]);

  const usedCategories = useMemo(() => {
    const s = new Set<string>();
    for (const g of open) if (g.category) s.add(g.category);
    return [...s].sort();
  }, [open]);

  const visible = useMemo(() => {
    const rows = open.filter((g) => {
      if (priority !== 'all' && String(g.priority) !== priority) return false;
      if (category !== 'all' && (g.category ?? '') !== category) return false;
      if (layer !== 'all') {
        const l = g.category ? catMeta.get(g.category)?.layer : undefined;
        if (l !== layer) return false;
      }
      return true;
    });

    // A gap with no price sorts last on both price orders rather than counting as zero — the
    // same reason the total below only sums what carries a figure.
    const lo = (g: WardrobeGap) => g.price_low_cents ?? g.price_high_cents;
    const hi = (g: WardrobeGap) => g.price_high_cents ?? g.price_low_cents;

    const cmp: Record<SortKey, (a: WardrobeGap, b: WardrobeGap) => number> = {
      unblocks: (a, b) => b.unblocks - a.unblocks || a.priority - b.priority,
      priority: (a, b) => a.priority - b.priority || b.unblocks - a.unblocks,
      'price-low': (a, b) =>
        (lo(a) ?? Infinity) - (lo(b) ?? Infinity) || a.description.localeCompare(b.description),
      'price-high': (a, b) =>
        (hi(b) ?? -Infinity) - (hi(a) ?? -Infinity) || a.description.localeCompare(b.description),
      category: (a, b) =>
        (a.category ?? 'zzz').localeCompare(b.category ?? 'zzz') || a.priority - b.priority,
      name: (a, b) => a.description.localeCompare(b.description),
    };
    return [...rows].sort(cmp[sort]);
  }, [open, priority, category, layer, sort, catMeta]);

  // Only count what carries a price. Treating an unpriced line as $0 would make the total read
  // lower than the truth, which is the one direction that matters when it is a budget.
  const lowTotal = visible.reduce((s, g) => s + (g.price_low_cents ?? g.price_high_cents ?? 0), 0);
  const highTotal = visible.reduce((s, g) => s + (g.price_high_cents ?? g.price_low_cents ?? 0), 0);
  const pricedCount = visible.filter(
    (g) => g.price_low_cents !== null || g.price_high_cents !== null
  ).length;

  async function analyse() {
    setWorking(true);
    setProblem(null);
    setNote(null);
    try {
      const r = await api.post<{ added: number; considered: number }>(
        '/wardrobe/gaps/analyse',
        {}
      );
      setNote(
        r.added === 0
          ? 'Nothing new — everything it would suggest is already on the list.'
          : `Added ${r.added} ${r.added === 1 ? 'thing' : 'things'} to the list.`
      );
      reload();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setWorking(false);
    }
  }

  async function dismiss(g: WardrobeGap) {
    await api.del(`/wardrobe/gaps/${g.id}`).catch(() => {});
    reload();
  }

  /** Patch one row in place — same reason as ItemsPanel: a refetch unmounts the open editor. */
  function patch(id: number, next: Partial<WardrobeGap>) {
    setData((cur) =>
      cur ? { ...cur, gaps: cur.gaps.map((g) => (g.id === id ? { ...g, ...next } : g)) } : cur
    );
  }

  const filtersOn = priority !== 'all' || category !== 'all' || layer !== 'all';

  return (
    <section className="space-y-5">
      <PanelHeading
        title="To buy"
        sub="What the wardrobe is missing, ranked by how much each purchase unlocks."
      />

      <Card tone="sunk">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-prose text-sm text-ink-soft">
            Reads everything you own against your style guide and every occasion, then names the
            single purchases that would fix the most. It won&rsquo;t repeat anything already on
            the list.
          </p>
          <Button variant="primary" onClick={analyse} disabled={working}>
            {working ? 'Working it out…' : "Work out what's missing"}
          </Button>
        </div>
      </Card>

      {problem && (
        <Card tone="over">
          <p className="text-sm text-ink">{problem}</p>
        </Card>
      )}
      {note && !problem && (
        <Card tone="ok">
          <p className="text-sm text-ink">{note}</p>
        </Card>
      )}

      {loading && <Loading label="Loading the list" rows={3} />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && open.length === 0 && (
        <EmptyState title="Nothing on the list">
          Press the button above, or keep an outfit that needs something you don&rsquo;t own yet
          &mdash; either way it lands here.
        </EmptyState>
      )}

      {open.length > 0 && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-display text-lg font-semibold text-ink">
              {visible.length} {visible.length === 1 ? 'thing' : 'things'}
              {filtersOn && <span className="text-ink-soft"> of {open.length}</span>}
            </span>
            {pricedCount > 0 && (
              <span className="text-sm text-ink-soft">
                about{' '}
                {lowTotal === highTotal
                  ? money(lowTotal)
                  : `${money(lowTotal)}–${money(highTotal)}`}
                {pricedCount < visible.length &&
                  ` for the ${pricedCount} with a price on ${pricedCount === 1 ? 'it' : 'them'}`}
              </span>
            )}
          </div>

          {/* Controls. Category and layer are both offered because they answer different
              questions — "what belts do I still need" and "why is my outer layer empty". */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-soft">
            <label className="flex items-center gap-2">
              <span>Sort</span>
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="w-auto"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex items-center gap-2">
              <span>Priority</span>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-auto"
              >
                <option value="all">All</option>
                <option value="1">Buy next</option>
                <option value="2">Worth having</option>
                <option value="3">Refinement</option>
              </Select>
            </label>

            {layers.length > 1 && (
              <label className="flex items-center gap-2">
                <span>Layer</span>
                <Select
                  value={layer}
                  onChange={(e) => setLayer(e.target.value)}
                  className="w-auto"
                >
                  <option value="all">All</option>
                  {layers.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Select>
              </label>
            )}

            <label className="flex items-center gap-2">
              <span>Category</span>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-auto"
              >
                <option value="all">All</option>
                {usedCategories.map((c) => (
                  <option key={c} value={c}>
                    {catMeta.get(c)?.label ?? c}
                  </option>
                ))}
              </Select>
            </label>

            <div className="ml-auto flex items-center gap-1">
              <Button
                onClick={() => setView('cards')}
                aria-pressed={view === 'cards'}
                className={cx(view === 'cards' && 'border-sage bg-sage-soft text-sage')}
              >
                Cards
              </Button>
              <Button
                onClick={() => setView('table')}
                aria-pressed={view === 'table'}
                className={cx(view === 'table' && 'border-sage bg-sage-soft text-sage')}
              >
                List
              </Button>
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="Nothing matches that filter"
              action={
                <Button
                  onClick={() => {
                    setPriority('all');
                    setCategory('all');
                    setLayer('all');
                  }}
                >
                  Clear filters
                </Button>
              }
            >
              {open.length} {open.length === 1 ? 'thing' : 'things'} on the list, none in this
              combination.
            </EmptyState>
          ) : view === 'table' ? (
            <GapTable
              gaps={visible}
              catMeta={catMeta}
              onEdit={setEditing}
              onBuy={setBuying}
              onRemove={dismiss}
            />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {visible.map((g) => (
                <GapCard
                  key={g.id}
                  gap={g}
                  categoryLabel={g.category ? (catMeta.get(g.category)?.label ?? g.category) : null}
                  onEdit={() => setEditing(g.id)}
                  onBuy={() => setBuying(g.id)}
                  onRemove={() => dismiss(g)}
                  onPhoto={(key) => patch(g.id, { image_r2_key: key, has_image: true })}
                />
              ))}
            </ul>
          )}

          {editing !== null && (
            <GapEditor
              gap={open.find((g) => g.id === editing)!}
              categories={data!.categories}
              onClose={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                reload();
              }}
            />
          )}

          {buying !== null && (
            <PurchaseDialog
              gap={open.find((g) => g.id === buying)!}
              categories={data!.categories}
              onClose={() => setBuying(null)}
              onBought={(item) => {
                setBuying(null);
                setNote(
                  `${item.name} is in the wardrobe now. Add a photo of it from the Wardrobe tab.`
                );
                reload();
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- cards

function GapCard({
  gap,
  categoryLabel,
  onEdit,
  onBuy,
  onRemove,
  onPhoto,
}: {
  gap: WardrobeGap;
  categoryLabel: string | null;
  onEdit: () => void;
  onBuy: () => void;
  onRemove: () => void;
  onPhoto: (key: string) => void;
}) {
  const p = PRIORITY[gap.priority] ?? PRIORITY[2];
  const price = priceText(gap);
  const [broken, setBroken] = useState(false);

  return (
    <li>
      <Card className="flex h-full flex-col gap-3">
        <div className="flex gap-3">
          {/* Square, not a strip: a garment you do not own yet is still a picture of a garment. */}
          <span className="block h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-surface-sunk">
            {gap.has_image && !broken ? (
              <img
                src={gapPhotoSrc(gap)}
                alt={gap.description}
                loading="lazy"
                onError={() => setBroken(true)}
                className="h-full w-full object-cover"
              />
            ) : (
              <GapPhotoField gap={gap} onUploaded={onPhoto} compact />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <h4 className="font-display text-base leading-snug font-semibold text-ink">
              {gap.description}
            </h4>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Chip tone={p.tone}>{p.label}</Chip>
              {categoryLabel && <Chip>{categoryLabel}</Chip>}
              {gap.unblocks > 0 && (
                <Chip tone="ok">
                  unblocks {gap.unblocks} {gap.unblocks === 1 ? 'outfit' : 'outfits'}
                </Chip>
              )}
            </div>
            <p className="mt-1.5 text-sm font-medium text-ink">
              {price ?? <span className="text-ink-soft italic">no price yet</span>}
            </p>
          </div>
        </div>

        {gap.notes && (
          <p className="max-w-prose text-sm leading-relaxed text-ink-soft">{gap.notes}</p>
        )}

        <ShopRow gap={gap} />

        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <Button variant="primary" onClick={onBuy}>
            I bought this
          </Button>
          <Button onClick={onEdit}>Edit</Button>
          <Button variant="ghost" className="ml-auto" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </Card>
    </li>
  );
}

/**
 * Where to look. A pasted product page wins the top slot; the searches always show, because
 * they are the thing that cannot go stale.
 */
function ShopRow({ gap }: { gap: WardrobeGap }) {
  if (!gap.url && gap.search_links.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      {gap.url && (
        <a
          href={gap.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-sage underline underline-offset-2"
        >
          The one you found &rarr;
        </a>
      )}
      {gap.search_links.length > 0 && (
        <>
          <span className="text-xs text-ink-soft">search</span>
          {gap.search_links.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-ink-soft underline underline-offset-2 hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- table

function GapTable({
  gaps,
  catMeta,
  onEdit,
  onBuy,
  onRemove,
}: {
  gaps: WardrobeGap[];
  catMeta: Map<string, WardrobeCategory>;
  onEdit: (id: number) => void;
  onBuy: (id: number) => void;
  onRemove: (g: WardrobeGap) => void;
}) {
  return (
    // Scrolls inside itself. The page body must never scroll sideways on a phone.
    <div className="overflow-x-auto rounded-card border border-border">
      <table className="w-full min-w-[46rem] border-collapse text-sm">
        <thead>
          <tr className="bg-surface-sunk text-left">
            <th className="px-3 py-2 font-semibold text-ink">What</th>
            <th className="px-3 py-2 font-semibold text-ink">Category</th>
            <th className="px-3 py-2 font-semibold text-ink">Priority</th>
            <th className="px-3 py-2 text-right font-semibold text-ink">Unlocks</th>
            <th className="px-3 py-2 text-right font-semibold text-ink">Price</th>
            <th className="px-3 py-2 font-semibold text-ink">Look</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {gaps.map((g) => {
            const p = PRIORITY[g.priority] ?? PRIORITY[2];
            const price = priceText(g);
            return (
              <tr key={g.id} className="border-t border-border align-top">
                <td className="px-3 py-2 font-medium text-ink">{g.description}</td>
                <td className="px-3 py-2 text-ink-soft">
                  {g.category ? (catMeta.get(g.category)?.label ?? g.category) : '—'}
                </td>
                <td className="px-3 py-2">
                  <Chip tone={p.tone}>{p.label}</Chip>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-soft">
                  {g.unblocks || '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink">{price ?? '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    {g.url && (
                      <a
                        href={g.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sage underline underline-offset-2"
                      >
                        found
                      </a>
                    )}
                    {g.search_links.slice(0, 3).map((l) => (
                      <a
                        key={l.label}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-ink-soft underline underline-offset-2 hover:text-ink"
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button variant="primary" onClick={() => onBuy(g.id)}>
                      Bought
                    </Button>
                    <Button onClick={() => onEdit(g.id)}>Edit</Button>
                    <Button variant="ghost" onClick={() => onRemove(g)}>
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- editor

/** Every field here was guessed by a model, so every field here is editable. */
function GapEditor({
  gap,
  categories,
  onClose,
  onSaved,
}: {
  gap: WardrobeGap;
  categories: WardrobeCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [description, setDescription] = useState(gap.description);
  const [category, setCategory] = useState(gap.category ?? '');
  const [priority, setPriority] = useState(String(gap.priority));
  const [low, setLow] = useState(gap.price_low_cents === null ? '' : String(gap.price_low_cents / 100));
  const [high, setHigh] = useState(
    gap.price_high_cents === null ? '' : String(gap.price_high_cents / 100)
  );
  const [url, setUrl] = useState(gap.url ?? '');
  const [notes, setNotes] = useState(gap.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /** Dollars in the box, integer cents on the wire. Never a float in the database. */
  function toCents(v: string): number | null {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t.replace(/[$,]/g, ''));
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
  }

  async function save() {
    setErr(null);
    if (description.trim() === '') {
      setErr('It needs a description — one garment you could walk into a shop and buy.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/wardrobe/gaps/${gap.id}`, {
        description: description.trim(),
        category: category === '' ? null : category,
        priority: Number(priority),
        price_low_cents: toCents(low),
        price_high_cents: toCents(high),
        url: url.trim() === '' ? null : url.trim(),
        notes: notes.trim() === '' ? null : notes.trim(),
      });
      onSaved();
    } catch (e) {
      setErr(errorText(e));
      setSaving(false);
    }
  }

  return (
    <Card>
      <h3 className="mb-3 font-display text-base font-semibold text-ink">Edit — {gap.description}</h3>

      {err && (
        <p role="alert" className="mb-3 rounded-lg border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink">
          {err}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-ink">What it is</span>
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Category</span>
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">(none)</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Priority</span>
          <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="1">Buy next</option>
            <option value="2">Worth having</option>
            <option value="3">Refinement</option>
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Price from ($)</span>
          <TextInput value={low} onChange={(e) => setLow(e.target.value)} inputMode="decimal" placeholder="60" />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Price to ($)</span>
          <TextInput value={high} onChange={(e) => setHigh(e.target.value)} inputMode="decimal" placeholder="90" />
        </label>

        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-ink">Link to the one you found</span>
          <TextInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            inputMode="url"
          />
          <span className="mt-1 block text-xs text-ink-soft">
            Optional. The search links are always there — this is for a specific product page.
          </span>
        </label>

        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-ink">Why it&rsquo;s on the list</span>
          <TextInput value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- purchase

/**
 * The want becomes a garment.
 *
 * The description is a shopping phrase — "khaki or stone flat-front shorts" — and what actually
 * turned up is one specific thing in one specific colour. So this asks, rather than silently
 * creating an item named after an either/or.
 */
function PurchaseDialog({
  gap,
  categories,
  onClose,
  onBought,
}: {
  gap: WardrobeGap;
  categories: WardrobeCategory[];
  onClose: () => void;
  onBought: (item: WardrobeItem) => void;
}) {
  const [name, setName] = useState(gap.description);
  const [category, setCategory] = useState(gap.category ?? '');
  const [colors, setColors] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    setErr(null);
    if (name.trim() === '') {
      setErr('Give it the name you would use reaching for it.');
      return;
    }
    if (category === '') {
      setErr('Pick a category — the outfit engine sorts by it.');
      return;
    }
    setSaving(true);
    try {
      const item = await api.post<WardrobeItem>(`/wardrobe/gaps/${gap.id}/purchase`, {
        name: name.trim(),
        category,
        colors: colors
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onBought(item);
    } catch (e) {
      setErr(errorText(e));
      setSaving(false);
    }
  }

  return (
    <Card tone="ok">
      <h3 className="mb-1 font-display text-base font-semibold text-ink">
        You bought it — what arrived?
      </h3>
      <p className="mb-3 max-w-prose text-sm text-ink-soft">
        This adds it to the wardrobe and takes it off the list. The line stays linked to the
        garment, so any saved outfit that was waiting on it becomes wearable.
      </p>

      {err && (
        <p role="alert" className="mb-3 rounded-lg border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink">
          {err}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-ink">Name</span>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Category</span>
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Pick one…</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="block sm:col-span-3">
          <span className="mb-1 block text-sm font-medium text-ink">Colours</span>
          <TextInput
            value={colors}
            onChange={(e) => setColors(e.target.value)}
            placeholder="tan, camel"
          />
          <span className="mt-1 block text-xs text-ink-soft">
            Comma separated, main one first. Plain words.
          </span>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" onClick={confirm} disabled={saving}>
          {saving ? 'Adding…' : 'Add to the wardrobe'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
